import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { mergePublicTheme, publicThemeVars } from '../publicTheme';
import PublicNavbar from './PublicNavbar';
import { ReviewModal, CommentsModal } from './Reviews';
import { usePublicSocket } from '../hooks/usePublicSocket';

const center = { maxWidth: '480px', margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center', color: 'var(--pub-muted, #94a3b8)' };
const commentBtn ={ padding: '7px 16px', borderRadius: '7px', border: 'none', background: 'var(--pub-accent, #38bdf8)', color: 'var(--pub-bg, #0f0f0f)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700 };
const commentBtnGhost = { padding: '7px 16px', borderRadius: '7px', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', background: 'none', color: 'var(--pub-text, #ffffff)', cursor: 'pointer', fontSize: '0.85rem' };
const reactionPill = { display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '999px', background: 'var(--pub-bg, #0f0f0f)', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', color: 'var(--pub-text, #ffffff)', fontSize: '0.85rem', fontWeight: 600 };

function MediaItem({ m }) {
  if (m.type === 'image') {
    return <img src={m.url} alt="" style={{ width: '100%', maxHeight: '460px', objectFit: 'contain', borderRadius: '10px', background: 'var(--pub-bg, #0f0f0f)' }} />;
  }
  if (m.type === 'video') {
    return <video src={m.url} controls style={{ width: '100%', maxHeight: '460px', borderRadius: '10px', background: '#000' }} />;
  }
  // pdf
  return (
    <a href={m.url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', borderRadius: '10px', color: 'var(--pub-accent, #38bdf8)', textDecoration: 'none', background: 'var(--pub-bg, #0f0f0f)' }}>
      <span style={{ fontSize: '1.6rem' }}>📄</span>
      <span style={{ fontWeight: 600 }}>Ver documento PDF</span>
    </a>
  );
}

function PublicNews() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [commentsTarget, setCommentsTarget] = useState(null);
  const [reviewRefresh, setReviewRefresh] = useState(0);
  const [liveRefresh, setLiveRefresh] = useState(0);

  // Tiempo real: si el admin publica/edita una noticia o cambia la configuración
  // de la empresa, recargamos sin que el cliente tenga que recargar la página.
  usePublicSocket(slug, ['news_changed', 'company_changed'], () => setLiveRefresh(n => n + 1));

  useEffect(() => {
    fetch(`/api/public-news/${encodeURIComponent(slug)}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setStore)
      .catch(() => setNotFound(true));
  }, [slug, reviewRefresh, liveRefresh]);

  const theme = mergePublicTheme(store?.theme);

  if (notFound) return <main style={center}>Noticias no disponibles.</main>;
  if (!store) return <main style={center}>Cargando...</main>;

  const news = store.news || [];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.colors.background, color: theme.colors.text, ...publicThemeVars(theme.colors) }}>
    <PublicNavbar slug={slug} store={store} current="news" subtitle="Noticias" maxWidth="820px" />
    <main style={{ maxWidth: '820px', margin: '0 auto', padding: '0 1.5rem 1.5rem' }}>
      {news.length === 0 ? (
        <p style={{ color: theme.colors.textMuted, textAlign: 'center', marginTop: '2rem' }}>Aún no hay noticias publicadas.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.4rem' }}>
          {news.map(n => (
            <article key={n.id} style={{ background: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '12px', padding: '1.4rem' }}>
              <h2 style={{ color: theme.colors.text, margin: '0 0 0.4rem', fontSize: '1.25rem' }}>{n.title}</h2>
              {n.created_at && (
                <div style={{ color: theme.colors.textMuted, fontSize: '0.78rem', marginBottom: '0.8rem' }}>
                  Publicado el {new Date(n.created_at).toLocaleString('es-CO', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
              {n.description && <p style={{ color: theme.colors.text, lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: '0 0 1rem' }}>{n.description}</p>}
              {(n.media || []).length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                  {n.media.map((m, i) => <MediaItem key={i} m={m} />)}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem', paddingTop: '0.9rem', borderTop: `1px solid ${theme.colors.border}33` }}>
                <button onClick={() => setReviewTarget(n)} style={commentBtn}>💬 Comentar</button>
                <button onClick={() => setCommentsTarget(n)} style={commentBtnGhost}>
                  Ver comentarios{(n.comments || []).length ? ` (${n.comments.length})` : ''}
                </button>
                {/* Reacciones estilo YouTube: número de 👍 y de 👎 que ha recibido la noticia */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginLeft: 'auto' }}>
                  <span style={reactionPill} title="Me gusta">👍 {n.likes || 0}</span>
                  <span style={reactionPill} title="No me gusta">👎 {n.dislikes || 0}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {reviewTarget && (
        <ReviewModal
          mode="news" slug={slug} target={reviewTarget}
          onClose={() => setReviewTarget(null)}
          onSubmitted={() => setReviewRefresh(n => n + 1)}
        />
      )}
      {commentsTarget && (
        <CommentsModal
          withRating={false} heading={`Comentarios · ${commentsTarget.title}`}
          comments={commentsTarget.comments || []}
          onClose={() => setCommentsTarget(null)}
        />
      )}
    </main>
    </div>
  );
}

export default PublicNews;
